import React, { useEffect, useState, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import PostCard from './PostCard';
import '../styles/Feed.css';

const SkeletonCard = () => (
    <div className="feed-skeleton-card">
        <div className="feed-skeleton-header">
            <div className="feed-skeleton-line feed-skeleton-line--name" />
            <div className="feed-skeleton-line feed-skeleton-line--meta" />
        </div>
        <div className="feed-skeleton-body">
            <div className="feed-skeleton-line feed-skeleton-line--full" />
            <div className="feed-skeleton-line feed-skeleton-line--full" />
            <div className="feed-skeleton-line feed-skeleton-line--short" />
        </div>
        <div className="feed-skeleton-footer" />
    </div>
);

const FeedSkeleton = () => (
    <div className="feed-skeleton">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
    </div>
);

const Feed = () => {
    const [posts,       setPosts]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState(null);
    const [actionError, setActionError] = useState('');

    const { user } = useContext(AuthContext);

    const showActionError = useCallback((msg) => {
        setActionError(msg);
        setTimeout(() => setActionError(''), 4000);
    }, []);

    useEffect(() => {
        if (!user) return;

        const fetchPosts = async () => {
            try {
                setLoading(true);
                const response = await api.get('/posts/nearby');
                setPosts(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching posts:', err);
                setError('Failed to load posts. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, [user]);

    const handleInteraction = async (postId, type, details, price) => {
        try {
            const response = await api.post(`/posts/${postId}/interact`, { type, details, price });
            const newInteraction = response.data;

            setPosts(prev => prev.map(post =>
                post._id === postId
                    ? {
                        ...post,
                        interactions: [
                            ...post.interactions,
                            {
                                ...newInteraction,
                                user: { _id: user.user._id, name: user.user.name },
                            },
                        ],
                    }
                    : post
            ));
        } catch (err) {
            console.error('Interaction failed:', err);
            showActionError('Failed to register interaction — please try again.');
        }
    };

    const handleComment = async (postId, content) => {
        try {
            const response = await api.post(`/posts/${postId}/comment`, { content });
            setPosts(prev => prev.map(post =>
                post._id === postId
                    ? { ...post, comments: [...post.comments, response.data] }
                    : post
            ));
        } catch (err) {
            console.error('Comment failed:', err);
            showActionError('Failed to post comment — please try again.');
        }
    };

    if (!user) return <div className="feed-empty">Please log in to view your feed.</div>;

    return (
        <div className="feed">
            <div className="feed-header">
                <h1 className="feed-title">Nearby Posts</h1>
                {!loading && posts.length > 0 && (
                    <span className="feed-count">{posts.length}</span>
                )}
            </div>

            {actionError && (
                <div className="feed-action-error" role="alert">{actionError}</div>
            )}

            {loading && <FeedSkeleton />}

            {!loading && error && (
                <div className="feed-error" role="alert">{error}</div>
            )}

            {!loading && !error && posts.length === 0 && (
                <div className="feed-empty">
                    <span className="feed-empty-icon">🏘️</span>
                    <p>No posts in your area yet.</p>
                    <p className="feed-empty-sub">Be the first to share something with your neighbours.</p>
                </div>
            )}

            {!loading && !error && posts.length > 0 && (
                <div className="feed-list">
                    {posts.map(post => (
                        <div
                            key={post._id}
                            className={`post-wrap${post.ai_categorization?.is_alert ? ' post-wrap--alert' : ''}`}
                        >
                            <PostCard
                                post={post}
                                user={user}
                                onInteraction={handleInteraction}
                                onComment={handleComment}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Feed;
